import cloneDeep from 'lodash/cloneDeep';
import escapeRegExp from 'lodash/escapeRegExp';
import { v4 as uuidv4 } from 'uuid';

export const FORM_ITEM_CONTROLS = [
    { text: 'Short Text', description: 'simple text', value: 'string', icon: 'text' },
    { text: 'Long Text', description: 'formatted paragraph', value: 'text', icon: 'align-left' },
    { text: 'Read-only', description: 'e.g., header/legalese/instructions', value: 'display', icon: 'quote-right' },
    { divider: true },
    { text: 'Single Checkbox', description: 'toggle on/off', value: 'boolean', icon: 'check-square' },
    { text: 'Radio Buttons', description: 'single selection - all options visible', value: 'radio-button', icon: 'dot-circle' },
    { text: 'Checkboxes', description: 'multi-select - all options visible', value: 'check-box', icon: 'tasks' },
    { text: 'Dropdown', description: 'single/multi-select - only selected option(s) visible', value: 'drop-down', icon: 'caret-square-down' },
    { divider: true },
    { text: 'Number', value: 'integer', icon: 'tally' },
    { text: 'Date', value: 'date', icon: 'calendar-day' },
];

// Item types a field cannot be switched to, so they are not offered in the type picker that
// FORM_ITEM_CONTROLS drives. A field set is a `question` item holding child fields. Sections
// have no icon of their own; callers fall back to their own default.
const FORM_CONTAINER_TYPES = {
    question: { text: 'Field set', icon: 'list-alt' },
    group: { text: 'Section' },
};

/**
 * Display text for a form item type ('string', 'drop-down', 'question', …), for read-only
 * type labels such as the catalog field-details dialog.
 *
 * @param {string} type - QuestionnaireItem type or item-control value
 * @returns {string|undefined} the label, or undefined for an unrecognized type
 */
export function getFormItemTypeText(type) {
    return FORM_ITEM_CONTROLS.find((control) => control.value === type)?.text || FORM_CONTAINER_TYPES[type]?.text;
}

/**
 * FontAwesome icon name for a form item type, covering the container types the type picker omits.
 *
 * @param {string} type - QuestionnaireItem type or item-control value
 * @returns {string|undefined} the icon name, or undefined for an unrecognized type
 */
export function getFormItemTypeIcon(type) {
    return FORM_ITEM_CONTROLS.find((control) => control.value === type)?.icon || FORM_CONTAINER_TYPES[type]?.icon;
}

// LOINC document types offered as a form's "Clinical form type" (non-live forms only).
export const CLINICAL_FORM_TYPES = [
    { code: '18842-5', system: 'http://loinc.org', display: 'Discharge summary' },
    { code: '11488-4', system: 'http://loinc.org', display: 'Consult note' },
    { code: '34117-2', system: 'http://loinc.org', display: 'History and physical note' },
    { code: '11506-3', system: 'http://loinc.org', display: 'Progress note' },
    { code: '28570-0', system: 'http://loinc.org', display: 'Procedure note' },
];

export const FORM_STATUS_OPTIONS = {
    draft: { text: 'Draft', color: 'info', icon: 'edit' },
    active: { text: 'Published', color: 'success', icon: 'book-bookmark' },
    retired: { text: 'Inactive', color: 'warning', icon: 'circle-pause' },
    unknown: { text: 'Locked', color: 'error', icon: 'lock-alt' },
};

export const ENABLE_WHEN_EXTERNAL_FIELDS = [
    { header: 'Patient' },
    {
        title: 'Sex',
        value: 'Patient.gender',
        options: [
            { title: 'Male', value: 'male' },
            { title: 'Female', value: 'female' },
            { title: 'Other', value: 'other' },
            { title: 'Unknown', value: 'unknown' },
        ],
    },
    {
        title: 'Gender Identity',
        value: 'Patient.genderIdentity',
        options: [
            {
                value: '446151000124109',
                title: 'Male',
            },
            {
                value: '446141000124107',
                title: 'Female',
            },
            {
                value: '407377005',
                title: 'Transgender-male',
            },
            {
                value: '407376001',
                title: 'Transgender-female',
            },
            {
                value: '446131000124102',
                title: 'Non-binary',
            },
            {
                value: 'OTH',
                title: 'Other',
            },
        ],
    },
    { title: 'Age', value: 'Patient.age', numeric: true },
    { title: 'Race', value: 'Patient.race' },
    { title: 'Ethnicity', value: 'Patient.ethnicity' },
];

// Rich-text toolbar sets for the builder's label editors, named so the control set cannot drift
// per call site again. They differ by surface on purpose — see the rich-text formatting section of
// specs/settings/form-builder.md; widening one is a product decision, not a refactor.

// A field's custom display text. No specialVars: substitution reads bnText, so a variable typed
// into profileAlternateText would reach the reader as a literal token.
export const RTE_TOOLBAR_FIELD_TEXT = ['bold', 'italic', 'underline'];
export const RTE_TOOLBAR_FIELD_LABEL = [...RTE_TOOLBAR_FIELD_TEXT, 'specialVars'];
export const RTE_TOOLBAR_LABEL = [...RTE_TOOLBAR_FIELD_TEXT, 'text-align', 'heading'];
export const RTE_TOOLBAR_DISPLAY_CONTENT = [...RTE_TOOLBAR_LABEL, 'list-ul', 'list-ol', 'specialVars'];

export const ENABLE_WHEN_OPERATORS = [
    { value: 'exists', title: 'Not Empty' },
    { value: '=', title: 'Equal To' },
    { value: '!=', title: 'Not Equal To' },
    { value: '>', title: 'Greater Than', type: 'integer' },
    { value: '<', title: 'Less Than', type: 'integer' },
    { value: '>=', title: 'Greater Than or Equal To', type: 'integer' },
    { value: '<=', title: 'Less Than or Equal To', type: 'integer' },
];

export function integerOnly(event) {
    let letter = new RegExp('^[a-zA-Z]$');
    // don't allow single letters (e is accepted by default because of Euler's number) or integer points
    if (letter.test(event.key) || event.key === '.') {
        event.preventDefault();
    }
}

// Item types that may legally carry answerOption/answerValueSet under FHIR que-5.
const QUE5_ANSWEROPTION_TYPES = new Set(['choice', 'open-choice', 'decimal', 'integer', 'date', 'dateTime', 'time', 'string', 'quantity']);

/**
 * Strips fields illegal for a QuestionnaireItem's current type so it passes the FHIR
 * invariants Aidbox validates on save (que-5/6/8/11). Idempotent; mutates in place.
 */
export function sanitizeQuestionnaireItem(item) {
    if (!item) return item;

    if (item.type === 'display') {
        // que-6: display can't carry required/repeats.
        item.required = undefined;
        item.repeats = undefined;
    }
    if (item.type === 'display' || item.type === 'group') {
        // que-8: display/group can't carry an initial value.
        item.initial = undefined;
    }
    if (!QUE5_ANSWEROPTION_TYPES.has(item.type)) {
        // que-5: answerOption/answerValueSet only on option-capable types.
        item.answerOption = undefined;
        item.answerValueSet = undefined;
    }
    if (Array.isArray(item.answerOption) && item.answerOption.length > 0) {
        // que-11: a non-empty answerOption can't coexist with `initial`. Legacy choice fields
        // kept their default in `initial`, so relocate it onto the matching option before
        // dropping it — unless an option is already selected, which then wins.
        if (item.type === 'choice' && Array.isArray(item.initial) && item.initial.length > 0 && !item.answerOption.some((option) => option?.initialSelected)) {
            const defaultCodes = new Set(item.initial.map((entry) => entry?.value?.string).filter((code) => code != null));
            item.answerOption.forEach((option) => {
                if (defaultCodes.has(option?.value?.Coding?.code)) {
                    option.initialSelected = true;
                }
            });
        }
        item.initial = undefined;
    }

    return item;
}

/**
 * True when an item's type has drifted away from 'display' due to catalog type-propagation:
 * a childless item carrying a displayCategory should be display. Display items link to
 * the catalog through the questionnaireField extension, never a top-level code (que-3),
 * so an item holding a code is a catalog-backed input field with a stale displayCategory
 * and must keep both its type and its code — dropping the code would sever the field's
 * description and hide it from the catalog's in-use check. Coded items are repaired
 * server-side instead, where the linked field's type can be read.
 *
 * Shared by the builder heal and the render-time guard so both agree on what drift is.
 *
 * @param {object} item - Questionnaire item (fhirworks model or plain object)
 * @returns {boolean}
 */
export function isDriftedDisplayItem(item) {
    return Boolean(item && item.type !== 'display' && item.type !== 'group' && item.displayCategory && !item.code?.length && !(Array.isArray(item.item) && item.item.length > 0));
}

/**
 * Whether a display item states no "Show on" choice at all.
 *
 * The form draws read-only text by its displayCategory, so on screen an item without one draws
 * nothing, while print states it anyway. Such items reach a form copied from one that carried
 * them — a heading over a section, a line of guidance — and without being drawn the builder
 * offers nothing to hover, open or hide. The builder draws them so someone can choose.
 *
 * The form title and score items are display items with no category that are drawn elsewhere.
 *
 * @param {object} item - Questionnaire item (fhirworks model or plain object)
 * @returns {boolean}
 */
export function isUnsetDisplayItem(item) {
    return Boolean(item && item.type === 'display' && !item.customWidget && !item.displayCategory && item.linkId !== 'title-section' && !item.linkId?.includes('score-'));
}

/**
 * Recursively sanitizes Questionnaire items against their resolved type so the draft
 * stays valid for autosave and existing broken data self-scrubs on next edit. Mutates
 * in place and returns the same array reference.
 */
export function normalizeQuestionnaireItems(items) {
    if (!Array.isArray(items)) return items;

    for (const item of items) {
        if (item && item.type === 'display' && Array.isArray(item.item) && item.item.length > 0) {
            // que-1: display items can't have children, so promote to group.
            console.warn('normalizeQuestionnaireItems: promoting display item with children to group', item.linkId);
            item.type = 'group';
        }
        // que-3: heal an item whose type drifted away from 'display' — see isDriftedDisplayItem.
        if (isDriftedDisplayItem(item)) {
            item.type = 'display';
        }
        // Strip fields illegal for the item's (now final) type. Runs after que-1/que-3
        // healing so a promoted or healed item is sanitized against its resolved type.
        if (item) {
            sanitizeQuestionnaireItem(item);
        }
        if (item && item.customWidget) {
            // Strip stale empty-string widget props that Aidbox rejects (422) on save.
            item.widgetProps?.clearEmptyValues?.();
        }
        if (item && Array.isArray(item.item) && item.item.length > 0) {
            normalizeQuestionnaireItems(item.item);
        }
    }

    return items;
}

// enableWhen targets like "substanceUseList.heroin" resolve against the base linkId (before the dot).
const LIST_WIDGET_RULE_PREFIXES = ['substanceUseList', 'allergiesList', 'familyMemberHistory', 'treatmentHistory', 'medicationList'];

// True for a clinical-list widget rule target ("<widgetLinkId>.<answer>"), as opposed to a bare
// linkId or an external "Patient.*" path. Shared with useEnableWhen so both resolvers agree.
export function isListWidgetRuleQuestion(question) {
    if (!question?.includes('.')) return false;
    return LIST_WIDGET_RULE_PREFIXES.includes(question.split('.')[0]);
}

// Recursively collects every linkId present in a Questionnaire item tree.
function collectLinkIds(items, acc = new Set()) {
    if (!Array.isArray(items)) return acc;
    for (const item of items) {
        if (item?.linkId) acc.add(item.linkId);
        if (Array.isArray(item?.item) && item.item.length > 0) collectLinkIds(item.item, acc);
    }
    return acc;
}

/**
 * Removes enableWhen rules whose target field/section was deleted, so the "display rules" chip and
 * nav gating reflect the rules that remain. Resolution mirrors the editor: a bare linkId must exist,
 * a list-widget path resolves against its base linkId, "Patient.*" paths are kept. Mutates in place.
 */
export function scrubOrphanEnableWhenRules(items) {
    if (!Array.isArray(items)) return items;

    const linkIds = collectLinkIds(items);

    const resolves = (question) => {
        if (!question) return true;
        if (!question.includes('.')) return linkIds.has(question);
        if (isListWidgetRuleQuestion(question)) return linkIds.has(question.split('.')[0]);
        return true; // Patient.* or other external path — not an orphan
    };

    const walk = (nodes) => {
        for (const item of nodes) {
            if (!item) continue;
            if (Array.isArray(item.enableWhen) && item.enableWhen.length > 0) {
                const kept = item.enableWhen.filter((rule) => resolves(rule?.question));
                if (kept.length !== item.enableWhen.length) {
                    if (kept.length > 0) {
                        item.enableWhen = kept;
                    } else {
                        item.enableWhen = undefined;
                        item.enableBehavior = undefined;
                    }
                }
            }
            if (Array.isArray(item.item) && item.item.length > 0) walk(item.item);
        }
    };
    walk(items);

    return items;
}

/**
 * Removes the item with the given linkId (top level or one level inside a section) and scrubs any
 * rule the removal orphaned. Mutates in place; returns true if removed. Intended as the one
 * delete-time entry point — the builder's own delete paths are routed through it in #4450, so
 * nothing on this branch calls it yet.
 */
export function removeQuestionnaireItem(items, linkId) {
    if (!Array.isArray(items) || !linkId) return false;

    let removed = false;
    const rootIndex = items.findIndex((el) => el?.linkId === linkId);
    if (rootIndex > -1) {
        items.splice(rootIndex, 1);
        removed = true;
    } else {
        const section = items.find((s) => Array.isArray(s?.item) && s.item.some((f) => f?.linkId === linkId));
        if (section) {
            const sectionItemIndex = section.item.findIndex((f) => f?.linkId === linkId);
            if (sectionItemIndex > -1) {
                section.item.splice(sectionItemIndex, 1);
                removed = true;
            }
        }
    }

    if (removed) scrubOrphanEnableWhenRules(items);
    return removed;
}

/**
 * Recursively finds group items with no children — que-1 requires at least one, so an empty group
 * blocks persistence. Signature blocks are excluded (they are display items, not type "group").
 */
export function findEmptyGroupItems(items) {
    const empty = [];
    if (!Array.isArray(items)) return empty;

    for (const item of items) {
        if (!item) continue;
        if (item.type === 'group') {
            if (!Array.isArray(item.item) || item.item.length === 0) {
                empty.push({ linkId: item.linkId, text: item.text || '' });
            } else {
                empty.push(...findEmptyGroupItems(item.item));
            }
        } else if (Array.isArray(item.item) && item.item.length > 0) {
            empty.push(...findEmptyGroupItems(item.item));
        }
    }

    return empty;
}

/**
 * Recursively finds scoring widgets (display items whose linkId contains "score-") with a blank
 * fhirpath expression — the Expression extension requires a non-empty value, so Aidbox 422s an
 * empty one. Happens when no fields are selected yet, or all selected fields were removed.
 */
export function findEmptyScoreWidgets(items) {
    const empty = [];
    if (!Array.isArray(items)) return empty;

    for (const item of items) {
        if (!item) continue;
        if (item.linkId?.includes('score-') && !item.scoreExpression?.expression?.trim()) {
            empty.push({ linkId: item.linkId, text: item.text || '' });
        }
        if (Array.isArray(item.item) && item.item.length > 0) {
            empty.push(...findEmptyScoreWidgets(item.item));
        }
    }

    return empty;
}

// True when a scoring widget survives anywhere in the tree, at any depth.
function hasScoreWidget(items) {
    if (!Array.isArray(items)) return false;
    return items.some((item) => item?.linkId?.includes('score-') || hasScoreWidget(item?.item));
}

/**
 * Clears the form-level scorable flag when a removal took the last scoring widget with it.
 * addScoreWidget turns the flag on, so without the matching removal the "Scored" chip outlives the
 * widget that earned it and the form can never stop advertising itself as scored. Mutates in place.
 *
 * Scoped to `removedItem` (a widget, or a section that held one) rather than re-derived on every
 * removal: the flag is also a manual checkbox — "A form can be marked as a scored questionnaire" —
 * so deleting an unrelated field must not revert a mark the user set by hand.
 */
export function scrubScorableFlag(form, removedItem) {
    if (!form?.scorable || !hasScoreWidget([removedItem])) return;
    if (!hasScoreWidget(form.item)) form.scorable = false;
}

/**
 * Regenerates every linkId in an item subtree so a copy doesn't collide with the original (que-2),
 * remapping `enableWhen[].question` (bare linkId or the "<linkId>.<widget>" prefix; not "Patient.*")
 * and `scoreExpression` tokens; `item.code` is left alone. Mutates in place.
 *
 * `mintLinkId` defaults to a uuid — canonicalizeItemIdentity passes an ordinal counter instead, to
 * reuse this remapping when comparing two subtrees for identity-insensitive equality.
 */
export function regenerateItemLinkIds(item, mintLinkId = () => uuidv4()) {
    const linkIdMap = {};

    // Pass 1: regenerate every linkId in the tree, recording old -> new. A widget takes its widget
    // name instead: it saves its answer under that name, so any other linkId leaves the form unable
    // to read the answer back. Widgets are one per form (findDuplicateWidgets), so it can't collide.
    const regenerateLinkIds = (node) => {
        if (!node) return;
        if (node.linkId) {
            const newLinkId = node.customWidget || mintLinkId();
            linkIdMap[node.linkId] = newLinkId;
            node.linkId = newLinkId;
        }
        if (Array.isArray(node.item)) node.item.forEach(regenerateLinkIds);
    };
    regenerateLinkIds(item);

    // A single alternation of every old linkId, longest first so a "score-<uuid>"
    // is matched before the bare uuid that could sit inside it. Each match is
    // replaced exactly once (newly-inserted linkIds are not re-scanned).
    const oldLinkIds = Object.keys(linkIdMap);
    const refPattern = oldLinkIds.length
        ? new RegExp(
              oldLinkIds
                  .sort((a, b) => b.length - a.length)
                  .map(escapeRegExp)
                  .join('|'),
              'g',
          )
        : null;

    // Pass 2: remap via the old -> new map. enableWhen keys on the part of `question` before the
    // first "." (widget suffixes and external Patient.* refs fall out of the same lookup);
    // scoreExpression embeds linkIds in a fhirpath string, so those swap token-by-token.
    const remapReferences = (node) => {
        if (!node) return;
        if (Array.isArray(node.enableWhen)) {
            node.enableWhen.forEach((rule) => {
                if (!rule?.question) return;
                const [prefix, ...rest] = rule.question.split('.');
                if (Object.prototype.hasOwnProperty.call(linkIdMap, prefix)) {
                    rule.question = [linkIdMap[prefix], ...rest].join('.');
                }
            });
        }
        if (refPattern && node.scoreExpression?.expression) {
            node.scoreExpression.expression = node.scoreExpression.expression.replace(refPattern, (match) => linkIdMap[match] ?? match);
        }
        if (Array.isArray(node.item)) node.item.forEach(remapReferences);
    };
    remapReferences(item);

    return item;
}

/**
 * Serializes field-set members with their linkIds replaced by document-order ordinals, so two
 * member arrays that differ only in their internal identifiers compare equal. Every reference into
 * the subtree is remapped through the same map, so text, type, choices, order, membership and rule
 * topology all still compare unequal — only identity is neutralized.
 *
 * Members arrive as fhirworks models whose properties are non-enumerable accessors, so the JSON
 * round-trip (which runs toJSON) must happen BEFORE the walk. cloneDeep here would yield "{}" per
 * member and silently degrade this to a member-count check.
 */
export function canonicalizeItemIdentity(items) {
    const clone = JSON.parse(JSON.stringify(items ?? []));
    let ordinal = 0;
    // The synthetic root carries no linkId, so only members and their descendants are tokenized.
    regenerateItemLinkIds({ item: clone }, () => `#${ordinal++}`);
    return JSON.stringify(clone);
}

/**
 * Deep-copies a section item for reuse, regenerating linkIds and remapping internal references
 * (see {@link regenerateItemLinkIds}). Pure: does not mutate the input.
 */
export function cloneSectionItem(sectionJson) {
    return regenerateItemLinkIds(cloneDeep(sectionJson));
}

/**
 * Recursively collects every item carrying a `customWidget` (a clinical widget),
 * in document order. Parent widgets are listed before their descendants.
 */
export function collectCustomWidgets(items) {
    const found = [];
    if (!Array.isArray(items)) return found;

    for (const item of items) {
        if (!item) continue;
        if (item.customWidget) {
            found.push({ linkId: item.linkId, customWidget: item.customWidget, text: item.text || '' });
        }
        if (Array.isArray(item.item) && item.item.length > 0) {
            found.push(...collectCustomWidgets(item.item));
        }
    }

    return found;
}

/**
 * Recursively finds clinical widgets appearing more than once. They are singletons — the same
 * `customWidget` twice gives colliding template linkIds (que-2), blocking save. Returns the
 * 2nd..Nth occurrence, not the first, so the descriptors point at what the user should remove.
 */
export function findDuplicateWidgets(items) {
    const seen = new Set();
    const duplicates = [];

    for (const widget of collectCustomWidgets(items)) {
        if (seen.has(widget.customWidget)) {
            duplicates.push(widget);
        } else {
            seen.add(widget.customWidget);
        }
    }

    return duplicates;
}

// Widgets excluded from the "Include in navigation panel" builder setting because
// they have system-driven navigation placement rather than user-controlled placement.
export const NAV_EXCLUDED_WIDGETS = ['encounterDetails', 'incidentReportGroup', 'incidentReportParticipant', 'formMemoInfo'];

// Returns true when the item is a widget excluded from the "Include in navigation
// panel" setting. Null-safe.
export function isNavExcludedWidget(item) {
    return NAV_EXCLUDED_WIDGETS.includes(item?.customWidget);
}

// Widgets that have been retired. They are no longer in the widget catalog, so a legacy
// placement on an existing form renders nothing; listing them here keeps the navigation
// panel from linking to the empty section it leaves behind.
export const RETIRED_WIDGETS = ['admittingDiagnosis'];

// Returns true when the item is a retired widget. Null-safe.
export function isRetiredWidget(item) {
    return RETIRED_WIDGETS.includes(item?.customWidget);
}
